import { faFilter } from '@fortawesome/pro-regular-svg-icons';
import { Flex, useMantineTheme } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Badge, Button, EmptyState, Icon, Spinner, Table, Text } from '@feathr/components';
import { useLocalUrl, useStore, useUser } from '@feathr/extender/state';
import { getIconForAction, useAnimatedRowProps, useHighlightedList } from '@feathr/hooks';

import type { IDonationCreatedEvent } from '../../hooks';
import { useFundraisingEvents } from '../../hooks';
import ExportDonationsButton from '../ExportDonationsButton';
import { getDefaultColumnIds, getVisibleColumns } from './TransactionColumns';
import TransactionColumnsDrawer from './TransactionColumnsDrawer';
import TransactionFiltersDrawer from './TransactionFiltersDrawer';
import useHistoricalDonations from './useHistoricalDonations';
import useTransactionFilters from './useTransactionFilters';

import * as styles from './TransactionTable.css';

const HIGHLIGHT_DURATION = 3000;
const USER_SETTING_KEY = 'displayed_transaction_columns';
const HISTORICAL_PAGE_SIZE = 10;

function TransactionTable(): JSX.Element {
  const { t } = useTranslation();
  const theme = useMantineTheme();
  const user = useUser();
  const localUrl = useLocalUrl();
  const { DonationForms } = useStore();
  const [columnIds, setColumnIds] = useState<string[]>(getDefaultColumnIds());
  const [isDrawerOpen, { open: openDrawer, close: closeDrawer }] = useDisclosure(false);
  const [isFiltersDrawerOpen, { open: openFiltersDrawer, close: closeFiltersDrawer }] =
    useDisclosure(false);

  // Filter state
  const { filters, setFilters, clearFilters, activeFilterCount, buildPredicates, matchesFilters } =
    useTransactionFilters();

  // Build predicates for API call
  const filterPredicates = buildPredicates();

  // Historical donations from Breadcrumbs API
  const {
    donations: historicalDonations,
    isLoading: isHistoricalLoading,
    page,
    tablePageIndex,
    totalPages,
    pageSize,
    sort,
    goToPage,
    onSortChange,
  } = useHistoricalDonations(HISTORICAL_PAGE_SIZE, filterPredicates);

  // Real-time donations from SSE events
  const { items: realtimeDonations, addItem } = useHighlightedList<IDonationCreatedEvent>({
    duration: HIGHLIGHT_DURATION,
  });

  const { getTrProps } = useAnimatedRowProps<IDonationCreatedEvent & { isNew?: boolean }>({
    animationClassName: styles.animateIn,
  });

  useFundraisingEvents({
    onDonationCreated: (event) => {
      if (matchesFilters(event)) {
        addItem(event);
      }
    },
  });

  // Get form options for filter dropdown
  const formsResponse = DonationForms.list({ pagination: { page_size: 1000 } });
  const formOptions = (formsResponse.models ?? []).map((form) => ({
    id: form.get('id'),
    name: form.get('name') || 'Unnamed Form',
  }));

  useEffect(() => {
    if (!user.isPending) {
      const saved = user.getSetting(USER_SETTING_KEY) as string[] | undefined;
      if (saved?.length) {
        setColumnIds(saved);
      }
    }
  }, [user.isPending]);

  function handleApplyColumns(newColumnIds: string[]): void {
    setColumnIds(newColumnIds);
    user.setSetting(USER_SETTING_KEY, newColumnIds);
    user.patchDirty();
  }

  // Table provides 0-based page index, convert to 1-based for hook
  const handlePageChange = useCallback(
    (newPageIndex: number): void => {
      goToPage(newPageIndex + 1);
    },
    [goToPage],
  );

  // Merge real-time events (prepended) with historical donations.
  // Design decisions:
  // - Real-time events appear at the top with animations, regardless of current sort order.
  //   This ensures new donations are immediately visible without requiring a page refresh.
  // - Real-time events only display on page 1. Users on page 2+ won't see new donations
  //   until they return to page 1 or the historical data refreshes.
  // - Deduplicate by ID to prevent showing the same donation twice when historical
  //   data refreshes and includes a recently received real-time event.
  // - Real-time events are marked with isRealtime: true for UI indicators (e.g., "New" pill).
  const mergedDonations = useMemo((): IDonationCreatedEvent[] => {
    if (page === 1) {
      // Dedupe: exclude historical donations that already appear in realtime
      const realtimeIds = new Set(realtimeDonations.map((d) => d.id));
      const dedupedHistorical = historicalDonations.filter((d) => !realtimeIds.has(d.id));
      // Mark real-time events for UI display
      const markedRealtime = realtimeDonations.map((d) => ({ ...d, isRealtime: true }));
      return [...markedRealtime, ...dedupedHistorical];
    }
    return historicalDonations;
  }, [realtimeDonations, historicalDonations, page]);

  const columns = getVisibleColumns(columnIds, t, localUrl);

  const filtersButton = (
    <Button key={'filters'} onClick={openFiltersDrawer} prefix={<Icon icon={faFilter} />}>
      {t('Filters')}
    </Button>
  );

  const filterElements: [React.ReactNode, React.ReactNode] = [
    null,
    <>
      {activeFilterCount > 0 ? (
        <Badge value={activeFilterCount}>{filtersButton}</Badge>
      ) : (
        filtersButton
      )}
      <Button key={'customize'} onClick={openDrawer} prefix={getIconForAction('customize')}>
        {t('Customize columns')}
      </Button>
      <ExportDonationsButton />
    </>,
  ];

  const hasActiveFilters = activeFilterCount > 0;
  const showEmptyState = !isHistoricalLoading && mergedDonations.length === 0;

  function renderContent(): JSX.Element {
    if (showEmptyState && hasActiveFilters) {
      return (
        <EmptyState
          description={t('Try adjusting your filters or clearing them to see all donations.')}
          label={t('No donations match your filters')}
        >
          <Button onClick={clearFilters} type={'secondary'}>
            {t('Clear filters')}
          </Button>
        </EmptyState>
      );
    }

    if (showEmptyState) {
      return (
        <EmptyState
          description={t('Donations will appear here as they come in.')}
          label={t('No donation transactions yet')}
        />
      );
    }

    return (
      <Table<IDonationCreatedEvent>
        animateRows={true}
        className={styles.table}
        columns={columns}
        filterElements={filterElements}
        filterLabel={<Text.SmallSemibold>{t('Donation Transactions')}</Text.SmallSemibold>}
        getTrProps={getTrProps}
        idKey={'id'}
        isLoading={isHistoricalLoading}
        isPaginated={true}
        items={mergedDonations}
        manual={true}
        onPageChange={handlePageChange}
        onSortChange={onSortChange}
        page={tablePageIndex}
        pages={totalPages}
        pageSize={pageSize}
        sort={sort}
        wrapperMode={'simple'}
      />
    );
  }

  return (
    <div className={styles.root}>
      {renderContent()}
      {isHistoricalLoading && mergedDonations.length === 0 && (
        <Flex align={'center'} gap={theme.spacing['2']} justify={'center'} p={'4'}>
          <Spinner />
          <Text.DefaultRegular>{t('Loading transactions...')}</Text.DefaultRegular>
        </Flex>
      )}
      <TransactionColumnsDrawer
        columnIds={columnIds}
        isOpen={isDrawerOpen}
        onApply={handleApplyColumns}
        onClose={closeDrawer}
      />
      <TransactionFiltersDrawer
        filters={filters}
        formOptions={formOptions}
        isOpen={isFiltersDrawerOpen}
        onApply={setFilters}
        onClose={closeFiltersDrawer}
      />
    </div>
  );
}

export default observer(TransactionTable);
